// Clear the fixed Milla auth bar used by Narcity and MTLBlog, with a small
// visual gutter below it. The previous 16px offset let sticky creatives sit
// underneath the 51px bar.
const DEFAULT_STICKY_TOP_PX = 64;
const DEFAULT_MIN_RAIL_EXTRA_PX = 24;
const DEFAULT_VIEWPORT_GUTTER_PX = 16;
const MAX_UNCLIP_ANCESTOR_DEPTH = 25;

function readElementHeight(element) {
  if (!element) return 0;

  if (typeof element.getBoundingClientRect === 'function') {
    const rect = element.getBoundingClientRect();
    const height = Number(rect && rect.height);
    if (Number.isFinite(height) && height > 0) {
      return height;
    }
  }

  const measured = Math.max(
    Number(element.offsetHeight || 0),
    Number(element.clientHeight || 0)
  );
  return Number.isFinite(measured) ? measured : 0;
}

function readCreativeHeight(slot, size) {
  if (Array.isArray(size) && size.length >= 2) {
    const height = Number(size[1]);
    if (Number.isFinite(height) && height > 1) {
      return height;
    }
  }

  const iframe = slot && typeof slot.querySelector === 'function'
    ? slot.querySelector('iframe')
    : null;
  return readElementHeight(iframe) || readElementHeight(slot);
}

function restoreInlineStyle(target, property, value) {
  if (!target || !target.style) return;
  target.style[property] = value || '';
}

function canUseOverflowClip(runtimeWindow) {
  const cssApi = runtimeWindow && runtimeWindow.CSS;
  return Boolean(
    cssApi &&
    typeof cssApi.supports === 'function' &&
    cssApi.supports('overflow', 'clip')
  );
}

// position:sticky only moves relative to the nearest scroll container, and an
// ancestor with `overflow: hidden` is one — even though it never scrolls. The
// Rebelmouse article templates wrap the whole post body in
// `.widget { overflow: hidden }`, which freezes every sticky ad inside it.
// `overflow: clip` clips identically without creating a scroll container, so
// while an ad is stuck we convert non-scrolling hidden ancestors to clip.
// Browsers without clip support are left untouched (sticky stays frozen there,
// same as before). Conversions are refcounted so overlapping sticky slots in
// the same container don't undo each other.
function unclipStickyAncestors(slot, target, runtimeWindow) {
  if (
    !target ||
    !runtimeWindow ||
    typeof runtimeWindow.getComputedStyle !== 'function' ||
    !canUseOverflowClip(runtimeWindow)
  ) {
    return;
  }

  const doc = runtimeWindow.document || {};
  const modified = [];
  let el = target.parentElement;
  let depth = 0;

  while (el && el !== doc.body && el !== doc.documentElement && depth < MAX_UNCLIP_ANCESTOR_DEPTH) {
    let styles = null;
    try {
      styles = runtimeWindow.getComputedStyle(el);
    } catch (_) {
      break;
    }
    const isNonScrollingHidden =
      styles &&
      styles.overflowX === 'hidden' &&
      styles.overflowY === 'hidden' &&
      !Number(el.scrollTop) &&
      !Number(el.scrollLeft);

    if (Number(el._millaOverflowClipCount) > 0) {
      // Already converted for another sticky slot in the same container;
      // claim a reference so that slot clearing doesn't restore `hidden`
      // underneath this one.
      el._millaOverflowClipCount += 1;
      modified.push(el);
    } else if (isNonScrollingHidden && el.style) {
      el._millaOverflowClipPrev = el.style.overflow;
      el.style.overflow = 'clip';
      el._millaOverflowClipCount = 1;
      modified.push(el);
    }

    el = el.parentElement;
    depth += 1;
  }

  if (modified.length) {
    slot._millaContentAdStickyUnclippedAncestors = modified;
  }
}

function releaseUnclippedAncestors(slot) {
  const els = slot && slot._millaContentAdStickyUnclippedAncestors;
  if (!Array.isArray(els)) return;
  els.forEach((el) => {
    if (!el) return;
    const count = Number(el._millaOverflowClipCount || 0) - 1;
    if (count > 0) {
      el._millaOverflowClipCount = count;
      return;
    }
    el._millaOverflowClipCount = 0;
    if (el.style) {
      el.style.overflow = el._millaOverflowClipPrev || '';
    }
    el._millaOverflowClipPrev = null;
  });
  slot._millaContentAdStickyUnclippedAncestors = null;
}

function clearContentAdSticky(slot) {
  if (!slot) return false;

  const target = slot._millaContentAdStickyTarget;
  const rail = slot._millaContentAdStickyRail;
  const previous = slot._millaContentAdStickyPreviousStyles || {};

  if (target) {
    restoreInlineStyle(target, 'position', previous.position);
    restoreInlineStyle(target, 'top', previous.top);
    if (target.classList) {
      target.classList.remove('milla-content-ad-sticky');
    }
  }

  if (slot._millaContentAdStickySlotResized) {
    restoreInlineStyle(slot, 'height', previous.slotHeight);
    restoreInlineStyle(slot, 'minHeight', previous.slotMinHeight);
    if (rail) {
      restoreInlineStyle(rail, 'minHeight', previous.railMinHeight);
    }
    slot._millaContentAdStickySlotResized = false;
  }

  releaseUnclippedAncestors(slot);

  if (rail) {
    if (rail.classList) {
      rail.classList.remove('milla-content-ad-sticky-rail');
    }
    if (rail.dataset) {
      delete rail.dataset.millaContentAdSticky;
    }
  }

  slot._millaContentAdStickyTarget = null;
  slot._millaContentAdStickyRail = null;
  slot._millaContentAdStickyPreviousStyles = null;
  return Boolean(target);
}

function resolveStickyPair(slot, creativeHeight, minRailExtraPx) {
  if (!slot || typeof slot.closest !== 'function') return null;

  const flex = slot.closest('.ad-flex');
  const wrapper = slot.closest('.ad-wrapper');
  const flexHeight = readElementHeight(flex);
  const wrapperHeight = readElementHeight(wrapper);

  if (flex && flexHeight >= creativeHeight + minRailExtraPx) {
    return { rail: flex, target: slot };
  }

  if (wrapper && wrapperHeight >= creativeHeight + minRailExtraPx) {
    return { rail: wrapper, target: flex || slot };
  }

  return null;
}

function applyContentAdSticky(slot, size, options = {}) {
  clearContentAdSticky(slot);

  if (
    !slot ||
    !slot.classList ||
    !slot.classList.contains('content-ad') ||
    (slot.dataset && slot.dataset.millaAdState === 'collapsed')
  ) {
    return false;
  }

  const stickyTopPx = Number.isFinite(Number(options.topPx))
    ? Math.max(0, Number(options.topPx))
    : DEFAULT_STICKY_TOP_PX;
  const minRailExtraPx = Number.isFinite(Number(options.minRailExtraPx))
    ? Math.max(1, Number(options.minRailExtraPx))
    : DEFAULT_MIN_RAIL_EXTRA_PX;
  const viewportGutterPx = Number.isFinite(Number(options.viewportGutterPx))
    ? Math.max(0, Number(options.viewportGutterPx))
    : DEFAULT_VIEWPORT_GUTTER_PX;
  const creativeHeight = readCreativeHeight(slot, size);

  if (!Number.isFinite(creativeHeight) || creativeHeight <= 1) {
    return false;
  }

  const runtimeWindow = options.runtimeWindow
    || (typeof window !== 'undefined' ? window : null);
  const viewportHeight = Number(runtimeWindow && runtimeWindow.innerHeight);
  if (
    Number.isFinite(viewportHeight) &&
    viewportHeight > 0 &&
    creativeHeight + stickyTopPx + viewportGutterPx > viewportHeight
  ) {
    return false;
  }

  const pair = resolveStickyPair(slot, creativeHeight, minRailExtraPx);
  if (!pair || !pair.target || !pair.target.style) {
    return false;
  }

  slot._millaContentAdStickyTarget = pair.target;
  slot._millaContentAdStickyRail = pair.rail;
  slot._millaContentAdStickyPreviousStyles = {
    position: pair.target.style.position,
    top: pair.target.style.top,
    slotHeight: slot.style ? slot.style.height : '',
    slotMinHeight: slot.style ? slot.style.minHeight : '',
    railMinHeight: pair.rail && pair.rail.style ? pair.rail.style.minHeight : ''
  };

  // A slot that keeps the rail's full reserved height (e.g. a 300x600
  // reservation that rendered a 300x250 creative) leaves the sticky target
  // no room to travel — the ad just sits at the top of the reservation. Pin
  // the rail at its reserved height, then size the slot down to the creative
  // so the ad can follow the scroll inside the reservation without shifting
  // the surrounding layout.
  const slotHeight = readElementHeight(slot);
  if (slot.style && slotHeight >= creativeHeight + 2) {
    const railHeight = readElementHeight(pair.rail);
    if (pair.rail && pair.rail.style && railHeight > 0) {
      pair.rail.style.minHeight = `${Math.round(railHeight)}px`;
    }
    slot.style.height = `${creativeHeight}px`;
    slot.style.minHeight = `${creativeHeight}px`;
    slot._millaContentAdStickySlotResized = true;
  }

  pair.target.style.position = 'sticky';
  pair.target.style.top = `${stickyTopPx}px`;
  unclipStickyAncestors(slot, pair.target, runtimeWindow);
  if (pair.target.classList) {
    pair.target.classList.add('milla-content-ad-sticky');
  }
  if (pair.rail.classList) {
    pair.rail.classList.add('milla-content-ad-sticky-rail');
  }
  if (pair.rail.dataset) {
    pair.rail.dataset.millaContentAdSticky = 'true';
  }

  return true;
}

module.exports = {
  DEFAULT_MIN_RAIL_EXTRA_PX,
  DEFAULT_STICKY_TOP_PX,
  applyContentAdSticky,
  clearContentAdSticky,
  readCreativeHeight,
  readElementHeight,
  resolveStickyPair
};
